import React, { useState } from 'react';
import { PlusIcon, FileUpIcon, MapPinIcon, UsersIcon, MapIcon, FileTextIcon, HardHatIcon, TruckIcon, AlertCircleIcon, BuildingIcon, CopyIcon } from 'lucide-react';
import { ActionCard } from '../../dashboard/ActionCard';
import { TabNavigation } from '../../dashboard/TabNavigation';
import { InProgressJobs } from './InProgressJobs';
import { CompletedJobs } from './CompletedJobs';
import { ImportJobsModal } from './ImportJobsModal';
import { CreateJobForm } from './CreateJobForm';
import { AssignJobsToBrokers } from './AssignJobsToBrokers';
import { AssignJobsToTrucks } from './AssignJobsToTrucks';
import { ForemanRequestsManager } from './ForemanRequestsManager';
import { JobMessageButton } from '../../messaging/JobMessageButton';
interface Broker {
  id: string;
  name: string;
  company: string;
  rating: number;
  truckCount: number;
  isDefault?: boolean;
  rates?: {
    'dump truck 10': number;
    'dump truck 12': number;
    'dump truck 14+': number;
    'strong arm 12': number;
    'strong arm 14': number;
    'strong arm 16+': number;
    'side dump': number;
    'super side 30tn+': number;
    'jumbo side 35tn+': number;
    'belly dump': number;
    'double belly': number;
  };
}
interface Job {
  id: string;
  jobName: string;
  jobNumber: string;
  date: string;
  material: string;
  numberOfTrucks: string;
  loadLocation: string;
  dumpLocation: string;
  foreman?: string;
  startTime?: string;
  truckType?: string;
  travelTime?: string;
  assignments?: {
    brokerId: string;
    truckCount: number;
  }[];
  status?: string;
  assignmentType?: 'broker' | 'direct' | 'undecided';
}
interface ContractorDashboardProps {
  onNavigate?: (page: string) => void;
}
export const ContractorDashboard = ({
  onNavigate
}: ContractorDashboardProps) => {
  const [activeTab, setActiveTab] = useState('jobs');
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreateJobForm, setShowCreateJobForm] = useState(false);
  const [pendingJobs, setPendingJobs] = useState<Job[]>([]);
  const [prefilledJobData, setPrefilledJobData] = useState<any>(null);
  // Brokers data that matches the manage brokers page
  const brokers: Broker[] = [{
    id: 'broker1',
    name: 'John Smith',
    company: 'Alpine Hauling',
    rating: 4.8,
    truckCount: 22,
    rates: {
      'dump truck 10': 95.0,
      'dump truck 12': 100.0,
      'dump truck 14+': 105.0,
      'strong arm 12': 110.0,
      'strong arm 14': 115.0,
      'strong arm 16+': 120.0,
      'side dump': 125.0,
      'super side 30tn+': 130.0,
      'jumbo side 35tn+': 135.0,
      'belly dump': 140.0,
      'double belly': 145.0
    }
  }, {
    id: 'dirt-logics-broker',
    name: 'Dirt Logics Broker',
    company: 'Dirt Logics Brokerage',
    rating: 4.9,
    truckCount: 25,
    isDefault: true,
    rates: {
      'dump truck 10': 93.0,
      'dump truck 12': 98.0,
      'dump truck 14+': 103.0,
      'strong arm 12': 108.0,
      'strong arm 14': 113.0,
      'strong arm 16+': 118.0,
      'side dump': 123.0,
      'super side 30tn+': 128.0,
      'jumbo side 35tn+': 133.0,
      'belly dump': 138.0,
      'double belly': 143.0
    }
  }];
  // Sample trucking companies data for direct assignment
  const truckingCompanies: TruckingCompany[] = [{
    id: 'trucking1',
    name: 'Mountain View Trucking',
    contactPerson: 'Bob Wilson',
    email: 'bob@mountainviewtrucking.com',
    phone: '555-234-5678',
    rating: 4.7,
    truckCount: 15,
    isDefault: true,
    rates: {
      'dump truck 10': 85.0,
      'dump truck 12': 90.0,
      'dump truck 14+': 95.0,
      'strong arm 12': 100.0,
      'strong arm 14': 105.0,
      'strong arm 16+': 110.0,
      'side dump': 115.0,
      'super side 30tn+': 120.0,
      'jumbo side 35tn+': 125.0,
      'belly dump': 130.0,
      'double belly': 135.0
    }
  }, {
    id: 'trucking2',
    name: 'Desert Haul Express',
    contactPerson: 'Maria Garcia',
    email: 'maria@deserthaulexpress.com',
    phone: '555-345-6789',
    rating: 4.5,
    truckCount: 12,
    rates: {
      'dump truck 10': 80.0,
      'dump truck 12': 85.0,
      'dump truck 14+': 90.0,
      'strong arm 12': 95.0,
      'strong arm 14': 100.0,
      'strong arm 16+': 105.0,
      'side dump': 110.0,
      'super side 30tn+': 115.0,
      'jumbo side 35tn+': 120.0,
      'belly dump': 125.0,
      'double belly': 130.0
    }
  }, {
    id: 'dirt-logics-trucking',
    name: 'Dirt Logics Trucking',
    contactPerson: 'Dirt Logics Trucking',
    email: 'trucking@dirtlogics.com',
    phone: '555-456-7890',
    rating: 4.9,
    truckCount: 18,
    rates: {
      'dump truck 10': 88.0,
      'dump truck 12': 93.0,
      'dump truck 14+': 98.0,
      'strong arm 12': 103.0,
      'strong arm 14': 108.0,
      'strong arm 16+': 113.0,
      'side dump': 118.0,
      'super side 30tn+': 123.0,
      'jumbo side 35tn+': 128.0,
      'belly dump': 133.0,
      'double belly': 138.0
    }
  }];
  // Sample data for in-progress jobs with tonnage tracking
  const inProgressJobs = [{
    id: '1',
    jobName: 'Downtown Commercial Complex Phase 1',
    jobNumber: 'DCC-2024-001',
    location: 'Downtown SLC → Granite Quarry',
    foreman: 'John Smith',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-1420">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-1421">
            <div className="bg-blue-600 h-2 rounded-full" style={{
          width: '75%'
        }} data-id="element-1422"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-1423">225/300 tons (75%)</span>
        </div>
  }, {
    id: '2',
    jobName: 'Highway 89 Expansion Project',
    jobNumber: 'HWY-2024-002',
    location: 'North Valley → Sand Pit',
    foreman: 'Mike Johnson',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-1424">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-1425">
            <div className="bg-blue-600 h-2 rounded-full" style={{
          width: '45%'
        }} data-id="element-1426"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-1427">90/200 tons (45%)</span>
        </div>
  }, {
    id: '3',
    jobName: 'Residential Development Phase 2',
    jobNumber: 'RES-2024-003',
    location: 'Sugar House → Gravel Yard',
    foreman: 'Sarah Williams',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-1428">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-1429">
            <div className="bg-blue-600 h-2 rounded-full" style={{
          width: '90%'
        }} data-id="element-1430"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-1431">135/150 tons (90%)</span>
        </div>
  }, {
    id: '4',
    jobName: 'Industrial Park Infrastructure',
    jobNumber: 'IND-2024-004',
    location: 'West Valley → Material Yard',
    foreman: 'Carlos Rodriguez',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-1432">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-1433">
            <div className="bg-blue-600 h-2 rounded-full" style={{
          width: '20%'
        }} data-id="element-1434"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-1435">50/250 tons (20%)</span>
        </div>
  }];
  // Sample data for broker-assigned jobs
  const brokerAssignedJobs = [{
    id: 'broker-job-1',
    jobName: 'East Valley Commercial Plaza',
    jobNumber: 'EVC-2024-005',
    location: 'East Valley → Rock Quarry',
    foreman: 'Alex Thompson',
    foremanPhone: '555-111-2222',
    broker: 'Alpine Hauling',
    brokerId: 'broker1',
    assignedTrucks: '3 trucks',
    startDate: '2024-01-15',
    estimatedHours: 8,
    truckType: 'dump truck 12',
    material: 'Gravel',
    customer: 'East Valley Development Corp',
    loadLocation: 'Rock Quarry North',
    dumpLocation: 'East Valley Commercial Plaza',
    startTime: '08:00',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-1436">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-1437">
            <div className="bg-indigo-600 h-2 rounded-full" style={{
          width: '60%'
        }} data-id="element-1438"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-1439">120/200 tons (60%)</span>
        </div>
  }, {
    id: 'broker-job-2',
    jobName: 'South Mountain Resort Development',
    jobNumber: 'SMR-2024-006',
    location: 'South Mountain → Resort Site',
    foreman: 'Jessica Miller',
    foremanPhone: '555-333-4444',
    broker: 'Dirt Logics Brokerage',
    brokerId: 'dirt-logics-broker',
    assignedTrucks: '5 trucks',
    startDate: '2024-01-16',
    estimatedHours: 10,
    truckType: 'strong arm 16+',
    material: 'Fill Dirt',
    customer: 'Mountain Resort Holdings',
    loadLocation: 'South Mountain Materials',
    dumpLocation: 'Resort Development Site',
    startTime: '07:30',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-1440">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-1441">
            <div className="bg-indigo-600 h-2 rounded-full" style={{
          width: '35%'
        }} data-id="element-1442"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-1443">105/300 tons (35%)</span>
        </div>
  }];
  // Sample data for directly assigned jobs
  const directAssignedJobs = [{
    id: 'direct-job-1',
    jobName: 'City Center Renovation',
    jobNumber: 'CCR-2024-007',
    location: 'Downtown → City Center',
    foreman: 'Mark Wilson',
    foremanPhone: '555-555-6666',
    truckingCompany: 'Mountain View Trucking',
    truckingCompanyId: 'trucking1',
    assignedTrucks: '2 trucks',
    startDate: '2024-01-14',
    estimatedHours: 6,
    truckType: 'dump truck 10',
    material: 'Concrete',
    customer: 'City Redevelopment Agency',
    loadLocation: 'Downtown Concrete Supply',
    dumpLocation: 'City Center Project',
    startTime: '07:00',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-1444">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-1445">
            <div className="bg-green-600 h-2 rounded-full" style={{
          width: '80%'
        }} data-id="element-1446"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-1447">160/200 tons (80%)</span>
        </div>
  }, {
    id: 'direct-job-2',
    jobName: 'West Park Landscaping Project',
    jobNumber: 'WPL-2024-008',
    location: 'West Valley → City Park',
    foreman: 'Daniel Garcia',
    foremanPhone: '555-777-8888',
    truckingCompany: 'Desert Haul Express',
    truckingCompanyId: 'trucking2',
    assignedTrucks: '3 trucks',
    startDate: '2024-01-15',
    estimatedHours: 7,
    truckType: 'dump truck 14+',
    material: 'Topsoil',
    customer: 'Parks Department',
    loadLocation: 'West Valley Soil Supply',
    dumpLocation: 'City Park West',
    startTime: '08:30',
    tonnageProgress: <div className="flex items-center gap-2" data-id="element-1448">
          <div className="w-24 bg-gray-200 rounded-full h-2" data-id="element-1449">
            <div className="bg-green-600 h-2 rounded-full" style={{
          width: '55%'
        }} data-id="element-1450"></div>
          </div>
          <span className="text-sm font-medium" data-id="element-1451">82/150 tons (55%)</span>
        </div>
  }];
  // Sample data for completed jobs
  const completedJobs = [{
    id: 'c1',
    name: 'Central District Commercial Building Phase 7',
    jobNumber: 'DC1006',
    date: '2023-08-11',
    broker: 'Alpine Hauling',
    truckingCompany: 'ABC Trucking',
    driver: 'Lisa Wong',
    truckType: 'Dump Truck',
    material: 'Sand',
    totalTons: '89.00',
    rate: '$98/ton',
    amount: '$872.00'
  }];
  const tabs = [{
    id: 'jobs',
    label: 'Jobs Overview',
    icon: <FileTextIcon size={18} data-id="element-1452" />
  }, {
    id: 'requests',
    label: 'Foreman Requests',
    icon: <AlertCircleIcon size={18} data-id="element-1453" />,
    badge: 3 // Number of pending requests
  }, {
    id: 'assign',
    label: 'Assign Jobs to Brokers',
    icon: <TruckIcon size={18} data-id="element-1454" />
  }, {
    id: 'assign-direct',
    label: 'Assign Jobs to Trucks',
    icon: <TruckIcon size={18} data-id="element-1455" />
  }];
  const handleCreateJobFromRequest = (request: any) => {
    // Convert foreman request to job data
    const jobData = {
      jobName: `${request.jobName} - Additional Materials`,
      jobNumber: `${request.jobNumber}-ADD-${Date.now().toString().slice(-4)}`,
      date: request.requestedDate,
      startTime: request.requestTime,
      material: request.material,
      numberOfTrucks: request.trucksRequested ? `${request.trucksRequested} trucks` : '1 truck',
      truckType: request.truckType,
      loadLocation: 'West Valley Materials Yard',
      dumpLocation: request.currentLocation,
      foreman: request.foremanName,
      rateType: 'hourly',
      hourlyRate: '',
      tonnageRate: '',
      targetLoads: request.trucksRequested?.toString() || '',
      targetTonnage: request.tonnageRequested?.toString() || '',
      comments: `Created from foreman request: ${request.reason}`,
      assignmentType: 'undecided'
    };
    setPrefilledJobData(jobData);
    setShowCreateJobForm(true);
  };
  // Handle creating a similar job from an existing broker-assigned job
  const handleCreateSimilarBrokerJob = (jobId: string) => {
    // Find the job in broker-assigned jobs
    const job = brokerAssignedJobs.find(j => j.id === jobId);
    if (job) {
      // Get tomorrow's date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowFormatted = tomorrow.toISOString().split('T')[0];
      // Create a new job number with current date
      const currentDate = new Date();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const jobNumberBase = job.jobNumber.split('-').slice(0, -1).join('-');
      const newJobNumber = `${jobNumberBase}-${month}${day}`;
      // Create prefilled data for the new job
      const prefilledData = {
        jobName: `${job.jobName} - Next Day`,
        jobNumber: newJobNumber,
        date: tomorrowFormatted,
        startTime: job.startTime || '08:00',
        material: job.material,
        numberOfTrucks: job.assignedTrucks,
        truckType: job.truckType,
        loadLocation: job.loadLocation,
        dumpLocation: job.dumpLocation,
        foreman: job.foreman,
        foremanPhone: job.foremanPhone,
        customer: job.customer,
        estimatedHours: job.estimatedHours,
        rateType: 'hourly',
        comments: `Recreated from job ${job.jobNumber}`,
        assignmentType: 'broker',
        geoPunchRequired: true,
        geoPunchDistance: 150
      };
      // Set the prefilled data and show the create job form
      setPrefilledJobData(prefilledData);
      setShowCreateJobForm(true);
    }
  };
  // Handle creating a similar job from an existing directly-assigned job
  const handleCreateSimilarDirectJob = (jobId: string) => {
    // Find the job in directly-assigned jobs
    const job = directAssignedJobs.find(j => j.id === jobId);
    if (job) {
      // Get tomorrow's date
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowFormatted = tomorrow.toISOString().split('T')[0];
      // Create a new job number with current date
      const currentDate = new Date();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const jobNumberBase = job.jobNumber.split('-').slice(0, -1).join('-');
      const newJobNumber = `${jobNumberBase}-${month}${day}`;
      // Create prefilled data for the new job
      const prefilledData = {
        jobName: `${job.jobName} - Next Day`,
        jobNumber: newJobNumber,
        date: tomorrowFormatted,
        startTime: job.startTime || '08:00',
        material: job.material,
        numberOfTrucks: job.assignedTrucks,
        truckType: job.truckType,
        loadLocation: job.loadLocation,
        dumpLocation: job.dumpLocation,
        foreman: job.foreman,
        foremanPhone: job.foremanPhone,
        customer: job.customer,
        estimatedHours: job.estimatedHours,
        rateType: 'hourly',
        comments: `Recreated from job ${job.jobNumber}`,
        assignmentType: 'direct',
        geoPunchRequired: true,
        geoPunchDistance: 150
      };
      // Set the prefilled data and show the create job form
      setPrefilledJobData(prefilledData);
      setShowCreateJobForm(true);
    }
  };
  const handleCreateJob = (jobData: any) => {
    console.log('Job created:', jobData);
    // Generate a unique ID for the new job
    const newJobId = `job-${Date.now()}`;
    // Create a new job object from the form data
    const newJob: Job = {
      id: newJobId,
      jobName: jobData.jobName,
      jobNumber: jobData.jobNumber,
      date: jobData.date,
      startTime: jobData.startTime,
      material: jobData.material,
      numberOfTrucks: jobData.numberOfTrucks,
      loadLocation: jobData.loadLocation,
      dumpLocation: jobData.dumpLocation,
      foreman: jobData.foreman,
      truckType: jobData.truckType,
      travelTime: jobData.travelTime,
      status: 'pending',
      assignmentType: jobData.assignmentType || 'undecided'
    };
    // Add the new job to the pending jobs
    setPendingJobs(prev => [...prev, newJob]);
    // Close the create job form and clear prefilled data
    setShowCreateJobForm(false);
    setPrefilledJobData(null);
    // Switch to the appropriate tab based on assignment type
    if (jobData.assignmentType === 'broker') {
      setActiveTab('assign');
    } else if (jobData.assignmentType === 'direct') {
      setActiveTab('assign-direct');
    } else {
      // If undecided, default to the assign tab
      setActiveTab('assign');
    }
  };
  const handleAssignJobs = (jobId: string, assignments: {
    brokerId: string;
    truckCount: number;
  }[]) => {
    console.log(`Job ${jobId} assigned:`, assignments);
    // Update the job with assignments and mark as assigned to broker
    setPendingJobs(prev => prev.map(job => job.id === jobId ? {
      ...job,
      assignments,
      status: 'assigned',
      assignmentType: 'broker' // Explicitly mark as broker assignment
    } : job));
    // In a real application, this would also:
    // 1. Send notifications to the assigned brokers
    // 2. Update the job status in the database
    // 3. Move the job to the in-progress jobs list
    // For this demo, we'll just show a success message
    alert(`Job successfully assigned to brokers`);
  };
  const handleAssignJobsToTrucks = (jobId: string, assignments: {
    truckingCompanyId: string;
    truckCount: number;
  }[]) => {
    console.log(`Job ${jobId} directly assigned to trucking companies:`, assignments);
    // Update the job with assignments and mark as directly assigned
    setPendingJobs(prev => prev.map(job => job.id === jobId ? {
      ...job,
      assignments,
      status: 'assigned-direct',
      assignmentType: 'direct' // Explicitly mark as direct assignment
    } : job));
    alert(`Job successfully assigned directly to trucking companies`);
  };
  const handleViewJobDetails = (jobId: string) => {
    console.log('View job details for:', jobId);
    alert(`Viewing details for job ${jobId}`);
  };
  const handleMessageForeman = (jobId: string) => {
    const job = inProgressJobs.find(j => j.id === jobId);
    console.log('Message foreman for:', jobId);
    alert(`Opening message dialog with foreman ${job?.foreman} for job ${jobId}`);
  };
  const handleCreateSimilarJob = (jobId: string) => {
    const job = inProgressJobs.find(j => j.id === jobId);
    console.log('Create similar job for:', jobId);
    alert(`Creating new job for tomorrow based on ${job?.jobName}`);
    // In a real application, this would:
    // 1. Open the create job form
    // 2. Pre-populate with the current job's details
    // 3. Set the date to the next day
    // 4. Allow the user to modify and save
  };
  // If showing create job form, render it instead of the dashboard
  if (showCreateJobForm) {
    return <div className="p-6 w-full" data-id="element-1456">
        <CreateJobForm onCancel={() => {
        setShowCreateJobForm(false);
        setPrefilledJobData(null);
      }} onSubmit={handleCreateJob} prefilledData={prefilledJobData} userRole="contractor" data-id="element-1457" />
      </div>;
  }
  return <div className="p-6 w-full" data-id="element-1458">
      <h1 className="text-2xl font-bold mb-6 text-center" data-id="element-1459">
        Contractor Dashboard
      </h1>
      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6" data-id="element-1460">
        <ActionCard icon={<PlusIcon size={20} data-id="element-1462" />} title="Create New Job" onClick={() => setShowCreateJobForm(true)} color="bg-blue-500 hover:bg-blue-600" data-id="element-1461" />
        <ActionCard icon={<FileUpIcon size={20} data-id="element-1464" />} title="Import Jobs" onClick={() => setShowImportModal(true)} color="bg-green-500 hover:bg-green-600" subAction={{
        label: 'View Import Format',
        onClick: () => setShowImportModal(true)
      }} data-id="element-1463" />
        <ActionCard icon={<MapPinIcon size={20} data-id="element-1466" />} title="Manage Job/Locations" onClick={() => onNavigate ? onNavigate('job-locations') : null} color="bg-purple-500 hover:bg-purple-600" data-id="element-1465" />
        <ActionCard icon={<HardHatIcon size={20} data-id="element-1468" />} title="Manage Foremen" onClick={() => onNavigate ? onNavigate('manage-foremen') : null} color="bg-orange-500 hover:bg-orange-600" data-id="element-1467" />
        <ActionCard icon={<UsersIcon size={20} data-id="element-1470" />} title="Manage Brokers" onClick={() => onNavigate ? onNavigate('manage-brokers') : null} color="bg-indigo-500 hover:bg-indigo-600" data-id="element-1469" />
      </div>
      {/* Tab Navigation */}
      <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-1471" />
      {/* Tab Content */}
      <div data-id="element-1472">
        {activeTab === 'jobs' && <>
            <InProgressJobs jobs={inProgressJobs} onViewDetails={handleViewJobDetails} onMessageForeman={handleMessageForeman} onCreateSimilarJob={handleCreateSimilarJob} data-id="element-1473" />
            {/* Broker-Assigned Jobs Section */}
            <div className="bg-white rounded-md shadow overflow-hidden mt-4" data-id="element-1474">
              <div className="flex items-center justify-between p-4 bg-gray-50" data-id="element-1475">
                <div className="flex items-center gap-2" data-id="element-1476">
                  <span className="h-5 w-5 rounded-full bg-indigo-500 flex items-center justify-center text-white" data-id="element-1477">
                    <BuildingIcon size={12} data-id="element-1478" />
                  </span>
                  <h3 className="font-medium" data-id="element-1479">Broker-Assigned Jobs</h3>
                  <span className="text-gray-500 text-sm" data-id="element-1480">
                    ({brokerAssignedJobs.length} active jobs)
                  </span>
                </div>
              </div>
              <div className="p-4" data-id="element-1481">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1482">
                  <thead className="bg-gray-50" data-id="element-1483">
                    <tr data-id="element-1484">
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1485">
                        Job Name
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1486">
                        Job #
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1487">
                        Location
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1488">
                        Foreman
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1489">
                        Broker
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1490">
                        Trucks
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1491">
                        Progress
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1492">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1493">
                    {brokerAssignedJobs.map(job => <tr key={job.id} className="hover:bg-gray-50" data-id="element-1494">
                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1495">
                          {job.jobName}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1496">
                          {job.jobNumber}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1497">
                          {job.location}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1498">
                          {job.foreman}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1499">
                          <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full" data-id="element-1500">
                            {job.broker}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1501">
                          {job.assignedTrucks}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1502">
                          {job.tonnageProgress}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1503">
                          <div className="flex space-x-2" data-id="element-1504">
                            <button className="text-green-600 hover:text-green-800 flex items-center" onClick={() => handleCreateSimilarBrokerJob(job.id)} data-id="element-1505">
                              <CopyIcon size={14} className="mr-1" data-id="element-1506" />
                              Recreate
                            </button>
                            <button className="text-blue-600 hover:text-blue-800" onClick={() => handleViewJobDetails(job.id)} data-id="element-1507">
                              View
                            </button>
                            <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="foreman" buttonText="Foreman" data-id="element-1508" />
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Directly Assigned Jobs Section */}
            <div className="bg-white rounded-md shadow overflow-hidden mt-4" data-id="element-1509">
              <div className="flex items-center justify-between p-4 bg-gray-50" data-id="element-1510">
                <div className="flex items-center gap-2" data-id="element-1511">
                  <span className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center text-white" data-id="element-1512">
                    <TruckIcon size={12} data-id="element-1513" />
                  </span>
                  <h3 className="font-medium" data-id="element-1514">Directly Assigned Jobs</h3>
                  <span className="text-gray-500 text-sm" data-id="element-1515">
                    ({directAssignedJobs.length} active jobs)
                  </span>
                </div>
              </div>
              <div className="p-4" data-id="element-1516">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1517">
                  <thead className="bg-gray-50" data-id="element-1518">
                    <tr data-id="element-1519">
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1520">
                        Job Name
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1521">
                        Job #
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1522">
                        Location
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1523">
                        Foreman
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1524">
                        Trucking Company
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1525">
                        Trucks
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1526">
                        Progress
                      </th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1527">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1528">
                    {directAssignedJobs.map(job => <tr key={job.id} className="hover:bg-gray-50" data-id="element-1529">
                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1530">
                          {job.jobName}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1531">
                          {job.jobNumber}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1532">
                          {job.location}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1533">
                          {job.foreman}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1534">
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full" data-id="element-1535">
                            {job.truckingCompany}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1536">
                          {job.assignedTrucks}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1537">
                          {job.tonnageProgress}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1538">
                          <div className="flex space-x-2" data-id="element-1539">
                            <button className="text-green-600 hover:text-green-800 flex items-center" onClick={() => handleCreateSimilarDirectJob(job.id)} data-id="element-1540">
                              <CopyIcon size={14} className="mr-1" data-id="element-1541" />
                              Recreate
                            </button>
                            <button className="text-blue-600 hover:text-blue-800" onClick={() => handleViewJobDetails(job.id)} data-id="element-1542">
                              View
                            </button>
                            <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="foreman" buttonText="Foreman" data-id="element-1543" />
                            <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="driver" buttonText="Drivers" data-id="element-1544" />
                            <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="trucking" buttonText="Trucking" data-id="element-1545" />
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
            <CompletedJobs jobs={completedJobs} data-id="element-1546" />
          </>}
        {activeTab === 'requests' && <ForemanRequestsManager onCreateJobFromRequest={handleCreateJobFromRequest} data-id="element-1547" />}
        {activeTab === 'assign' && <AssignJobsToBrokers jobs={pendingJobs.filter(job => job.status !== 'assigned' && job.status !== 'assigned-direct' && job.assignmentType !== 'direct')} brokers={brokers} onAssignJobs={handleAssignJobs} data-id="element-1548" />}
        {activeTab === 'assign-direct' && <AssignJobsToTrucks jobs={pendingJobs.filter(job => job.status !== 'assigned' && job.status !== 'assigned-direct' && job.assignmentType !== 'broker')} truckingCompanies={truckingCompanies} onAssignJobs={handleAssignJobsToTrucks} data-id="element-1549" />}
      </div>
      {/* Import Jobs Modal */}
      <ImportJobsModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} data-id="element-1550" />
    </div>;
};